import React from 'react';
import { theme } from 'antd';
import { useDispatch } from 'react-redux';

import AnnouncementBanner from '~components/AnnouncementBanner';
import { isImpersonatedSession } from '~features/auth';
import DynamicStateService, { DynamicStateScopes } from '~features/DynamicStateService';
import type { AiInsightsAnalyticsEvent } from './AiInsightsAnalyticsEvents';
import { AMAZON_LINEUP } from './aiCrawlerLineup';
import { markViewedEventSent, useIsViewedEventSent } from './CrawlerUpdateAnnouncementSlice';
import { useAiCrawlerLineup } from './useAiCrawlerLineup';
import useTrackAiInsightsPageEvent from './useTrackAiInsightsPageEvent';

const TITLE = 'We updated the AI Crawlers in AI Insights';
const BODY =
  'We added Amazon, one of the most active AI crawlers, for broader coverage. We also removed Gemini since Google-Extended is only a robots.txt setting that controls whether your content is used for Gemini. The crawling itself is done by Googlebot, so Gemini is no longer shown as a separate crawler.';

const CRAWLER_TILES = { src: '/assets/img/ai-insights/crawler-update-tiles.png', width: 352, height: 160 };

type CrawlerUpdateAnnouncementState = {
  dismissed?: boolean;
};

type DynamicStateUserWithCrawlerUpdateAnnouncement = {
  AiInsightsCrawlerUpdateAnnouncement?: CrawlerUpdateAnnouncementState;
};

type CrawlerUpdateAnnouncementVisibility = {
  visible: boolean;
  dismiss: () => void;
};

function useCrawlerUpdateAnnouncement(): CrawlerUpdateAnnouncementVisibility {
  const isOnAmazonLineup = useAiCrawlerLineup() === AMAZON_LINEUP;
  const [dynamicState, updateDynamicState] = DynamicStateService.useDynamicStateService<
    DynamicStateUserWithCrawlerUpdateAnnouncement,
    Record<string, never>
  >();
  const [closedThisVisit, setClosedThisVisit] = React.useState(false);
  const trackEvent = useTrackAiInsightsPageEvent();
  const isImpersonating = isImpersonatedSession();

  const isSavedStateKnown = dynamicState !== undefined;
  const isDismissed = dynamicState?.user.AiInsightsCrawlerUpdateAnnouncement?.dismissed === true;
  const visible = isOnAmazonLineup && isSavedStateKnown && !isDismissed && !closedThisVisit;

  const trackCustomerEvent = React.useCallback(
    (event: AiInsightsAnalyticsEvent) => {
      if (!isImpersonating) trackEvent(event);
    },
    [isImpersonating, trackEvent]
  );

  const isViewedEventSent = useIsViewedEventSent();
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!visible || isViewedEventSent) return;
    trackCustomerEvent('AI Insights: Crawler Update Banner Viewed');
    dispatch(markViewedEventSent());
  }, [visible, isViewedEventSent, trackCustomerEvent, dispatch]);

  const dismiss = () => {
    setClosedThisVisit(true);
    trackCustomerEvent('AI Insights: Crawler Update Banner Dismissed');
    if (isImpersonating) return;
    updateDynamicState({
      scope: DynamicStateScopes.User,
      state: { AiInsightsCrawlerUpdateAnnouncement: { dismissed: true } },
    });
  };

  return { visible, dismiss };
}

function CrawlerUpdateAnnouncement() {
  const { token } = theme.useToken();
  const { visible, dismiss } = useCrawlerUpdateAnnouncement();

  if (!visible) return null;

  return (
    <div style={{ marginBottom: token.marginLG }}>
      <AnnouncementBanner title={TITLE} body={BODY} illustration={CRAWLER_TILES} onDismiss={dismiss} />
    </div>
  );
}

export default CrawlerUpdateAnnouncement;
